import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import LocationInput from 'components/form/location_input/location_input';

const styles = require('./LocationField.module.scss');

type Context = 'talent' | 'company' | 'neutral';

interface LocationValue {
  city: string;
  country: string;
  countryCode: string;
}

export interface LocationFieldProps {
  autoSaveUrl?: string;
  containerClassName?: string;
  context?: Context;
  defaultValidationMessage?: string;
  defaultValue?: LocationValue;
  disabled?: boolean;
  error?: string;
  formName?: string;
  hint?: string;
  id?: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  onLocationChange?: (event: any) => void;
  onReset?: () => void;
  placeholder?: string;
  required?: boolean;
  value?: LocationValue;
}

function getCsrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  return meta?.content || '';
}

const LocationField: React.FC<LocationFieldProps> = ({
  autoSaveUrl,
  containerClassName,
  context = 'neutral',
  defaultValidationMessage,
  defaultValue,
  disabled,
  error,
  formName,
  hint,
  id = 'location-filter',
  label,
  labelSuffix,
  onLocationChange,
  onReset,
  placeholder = 'E.g. Lisbon',
  required,
  value,
}) => {
  const [displayError, setDisplayError] = useState(error || '');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayError(error || '');
  }, [error]);

  const saveLocation = useCallback(() => {
    if (!autoSaveUrl || !wrapperRef.current) return;

    const hiddenInputs = wrapperRef.current.querySelectorAll<HTMLInputElement>('input[type="hidden"]');
    const formData = new FormData();
    hiddenInputs.forEach((input) => {
      if (input.name) formData.append(input.name, input.value);
    });

    fetch(autoSaveUrl, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': getCsrfToken(), 'Accept': 'application/json' },
      body: formData,
    }).then((res) => {
      if (!res.ok) res.json().then((data) => {
        const err = data.errors?.city || data.errors?.country_code;
        if (err) setDisplayError(err);
      });
    });
  }, [autoSaveUrl]);

  const handleLocationChange = useCallback((event: any) => {
    setDisplayError('');
    onLocationChange?.(event);
    setTimeout(saveLocation, 100);
  }, [onLocationChange, saveLocation]);

  const handleReset = useCallback(() => {
    if (required) {
      setDisplayError(defaultValidationMessage);
    }
    onReset?.();
  }, [required, defaultValidationMessage, onReset]);

  const handleBlur = useCallback(() => {
    const input = wrapperRef.current?.querySelector<HTMLInputElement>('input:not([type="hidden"])');
    if (required && (!input?.value || input.value.trim() === '')) {
      setDisplayError(defaultValidationMessage);
    }
  }, [required, defaultValidationMessage]);

  const handleInput = useCallback(() => {
    setDisplayError('');
  }, []);

  const hasMessage = Boolean(displayError) || Boolean(hint);
  const messageId = hasMessage ? `${id}-message` : undefined;

  const locationInputClasses = classNames(
    styles.locationInput,
    {
      [styles[context]]: context,
      [styles.error]: Boolean(displayError),
      [styles.disabled]: disabled,
    }
  );

  return (
    <div className={classNames(styles.wrapper, containerClassName)}>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={id} className={styles.label}>{label}</label>
          {required && <span className={styles.required} aria-hidden="true">*</span>}
          {labelSuffix}
        </div>
      )}

      <div className={styles.inputWrapper}>
        <div
          ref={wrapperRef}
          onBlur={handleBlur}
          onInput={handleInput}
        >
          <LocationInput
            hideIcon={true}
            formName={formName}
            defaultValue={defaultValue}
            id={id}
            value={value}
            handleLocationChange={handleLocationChange}
            handleReset={handleReset}
            placeholder={placeholder}
            required={required}
            class={locationInputClasses}
          />
        </div>

        {hasMessage && (
          <div
            id={messageId}
            role={displayError ? 'alert' : undefined}
            className={classNames(styles.hintContainer, {
              [styles.hintError]: Boolean(displayError),
            })}
          >
            <Icon
              name={displayError ? 'triangleAlert' : 'info'}
              color={displayError ? 'error' : 'disable'}
              size="sm"
              aria-hidden="true"
            />
            <p className={styles.hintText}>{displayError || hint}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationField;
